import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, streamEvents, ApiError } from '@/api/client';
import { formatDateTime } from '@/i18n/format';
import { OPTIONS_KEY, type ChatTurn, type Effort, type ToolStep } from './assistant.api';

export const STARTERS = [
  'How many active employees do we have in each branch?',
  'Who was absent more than 3 days last month?',
  'Which leave requests are still waiting for approval?',
  'What was the total net pay in the last payroll?',
];

export interface ChatSettings {
  model: string;
  effort: Effort;
}

/**
 * The conversation itself: questions in, streamed answers out, with the
 * lookups the assistant made shown as it makes them — "looked at Attendance,
 * 31 rows" — so an answer can be traced to the data it came from.
 */
export function AssistantChat({
  conversationId,
  onConversation,
  settings,
}: {
  conversationId: string | null;
  onConversation: (id: string, model: string) => void;
  settings: ChatSettings;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  // The conversation this component is showing, so a reply that arrives after
  // switching chats is not written into the new one.
  const activeRef = useRef<string | null>(conversationId);

  // Opening a past conversation loads it; a fresh one starts empty.
  useEffect(() => {
    if (activeRef.current === conversationId && turns.length > 0) return;
    activeRef.current = conversationId;
    abortRef.current?.abort();
    if (!conversationId) {
      setTurns([]);
      return;
    }
    let cancelled = false;
    void api
      .get<{ turns: { role: 'user' | 'assistant'; text: string; tools: { name: string; input: unknown }[] }[] }>(
        `/assistant/conversations/${conversationId}`,
      )
      .then((data) => {
        if (cancelled) return;
        setTurns(mergeHistory(data.turns));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Leaving the page mid-answer stops the stream rather than letting it write
  // into a component that is gone.
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [turns]);

  const updateLast = (change: (turn: ChatTurn) => ChatTurn) =>
    setTurns((all) => {
      const copy = [...all];
      copy[copy.length - 1] = change(copy[copy.length - 1]);
      return copy;
    });

  const ask = async (question: string) => {
    const text = question.trim();
    if (!text || busy) return;
    setDraft('');
    setBusy(true);
    setTurns((all) => [
      ...all,
      { role: 'user', text, steps: [] },
      { role: 'assistant', text: '', thinking: '', steps: [], streaming: true },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamEvents(
        '/assistant/chat',
        {
          ...(activeRef.current ? { conversationId: activeRef.current } : { model: settings.model }),
          message: text,
          effort: settings.effort,
        },
        (event) => {
          switch (event.type) {
            case 'conversation':
              activeRef.current = event.id as string;
              onConversation(event.id as string, event.model as string);
              break;
            case 'thinking':
              updateLast((turn) => ({ ...turn, thinking: (turn.thinking ?? '') + (event.text as string) }));
              break;
            case 'text':
              updateLast((turn) => ({ ...turn, text: turn.text + (event.text as string) }));
              break;
            case 'tool_call':
              updateLast((turn) => ({
                ...turn,
                steps: [...turn.steps, { id: event.id as string, name: event.name as string, input: event.input, done: false }],
              }));
              break;
            case 'tool_result':
              updateLast((turn) => ({
                ...turn,
                steps: turn.steps.map((step) =>
                  step.id === event.id
                    ? {
                        ...step,
                        done: true,
                        isError: event.isError as boolean,
                        rowCount: event.rowCount as number | null,
                        table: event.table as ToolStep['table'],
                      }
                    : step,
                ),
              }));
              break;
            case 'error':
              updateLast((turn) => ({ ...turn, error: { code: event.code as string, message: event.message as string } }));
              if (event.code === 'assistant.not_connected') {
                void queryClient.invalidateQueries({ queryKey: OPTIONS_KEY });
              }
              break;
          }
        },
        controller.signal,
      );
    } catch (err) {
      if (!controller.signal.aborted) {
        const message = err instanceof ApiError ? err.message : t('Something went wrong while answering. Try again.');
        updateLast((turn) => ({ ...turn, error: { code: err instanceof ApiError ? (err.code ?? 'error') : 'error', message } }));
      }
    } finally {
      updateLast((turn) => ({ ...turn, streaming: false }));
      setBusy(false);
      abortRef.current = null;
      void queryClient.invalidateQueries({ queryKey: ['/assistant/conversations'] });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
        {turns.length === 0 ? (
          <Stack spacing={1.25} sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('Ask about your employees, attendance, leave or payroll. I only read — I never change anything, and I only see what you are allowed to see.')}
            </Typography>
            {STARTERS.map((starter) => (
              <Chip
                key={starter}
                label={t(starter)}
                onClick={() => void ask(t(starter))}
                variant="outlined"
                sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal' } }}
              />
            ))}
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {turns.map((turn, index) => (
              <Turn key={index} turn={turn} />
            ))}
            <div ref={endRef} />
          </Stack>
        )}
      </Box>

      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          void ask(draft);
        }}
        sx={{ borderTop: 1, borderColor: 'divider', p: 1.25, display: 'flex', gap: 1, alignItems: 'flex-end' }}
      >
        <TextField
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // Enter sends; Shift+Enter is a new line.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void ask(draft);
            }
          }}
          placeholder={t('Ask about your data…')}
          multiline
          maxRows={5}
          size="small"
          fullWidth
          disabled={busy}
          inputProps={{ maxLength: 4000 }}
        />
        {busy ? (
          <Tooltip title={t('Stop')}>
            <IconButton color="primary" onClick={() => abortRef.current?.abort()}>
              <StopIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={t('Send')}>
            <span>
              <IconButton color="primary" type="submit" disabled={!draft.trim()}>
                <SendIcon sx={{ transform: (theme) => (theme.direction === 'rtl' ? 'scaleX(-1)' : undefined) }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

function Turn({ turn }: { turn: ChatTurn }) {
  const { t } = useTranslation();
  const [showThinking, setShowThinking] = useState(false);

  if (turn.role === 'user') {
    return (
      <Box
        sx={{
          alignSelf: 'flex-end',
          maxWidth: '85%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          whiteSpace: 'pre-wrap',
          typography: 'body2',
        }}
      >
        {turn.text}
      </Box>
    );
  }

  const waiting = turn.streaming && !turn.text && turn.steps.length === 0;

  return (
    <Stack spacing={0.75} sx={{ maxWidth: '100%' }}>
      {turn.thinking && (
        <Box>
          <Button size="small" onClick={() => setShowThinking((open) => !open)} sx={{ p: 0, minWidth: 0, color: 'text.secondary', typography: 'caption' }}>
            {turn.streaming && !turn.text ? t('Thinking…') : t('How I worked it out')}
          </Button>
          <Collapse in={showThinking}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap', borderInlineStart: 2, borderColor: 'divider', ps: 1 }}>
              {turn.thinking}
            </Typography>
          </Collapse>
        </Box>
      )}

      {turn.steps.map((step) => (
        <Step key={step.id} step={step} />
      ))}

      {waiting && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('Thinking…')}
        </Typography>
      )}

      {turn.text && (
        <Box
          sx={{
            typography: 'body2',
            '& p': { my: 0.75 },
            '& table': { borderCollapse: 'collapse', my: 1, fontSize: 13, display: 'block', overflowX: 'auto' },
            '& th, & td': { border: 1, borderColor: 'divider', px: 1, py: 0.5, textAlign: 'start' },
            '& th': { bgcolor: 'action.hover' },
            '& ul, & ol': { my: 0.5, ps: 2.5 },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.text}</ReactMarkdown>
        </Box>
      )}

      {turn.error && (
        <Alert severity={turn.error.code === 'assistant.refused' ? 'info' : 'error'} sx={{ py: 0 }}>
          {errorMessage(turn.error, t)}
        </Alert>
      )}
    </Stack>
  );
}

/** One lookup, as a chip; with data behind it, a tap shows the rows. */
function Step({ step }: { step: ToolStep }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = stepLabel(step, t);
  const rows = step.table?.rows ?? [];

  return (
    <Box>
      <Chip
        size="small"
        icon={step.isError ? <ErrorOutlineIcon /> : <SearchIcon />}
        label={
          step.done
            ? step.rowCount !== null && step.rowCount !== undefined && !step.isError
              ? `${label} · ${t('{{count}} rows', { count: step.rowCount })}`
              : label
            : `${label}…`
        }
        color={step.isError ? 'warning' : 'default'}
        variant="outlined"
        onClick={rows.length > 0 ? () => setOpen((value) => !value) : undefined}
        sx={{ maxWidth: '100%' }}
      />
      {rows.length > 0 && (
        <Collapse in={open}>
          <Box sx={{ overflowX: 'auto', mt: 0.75, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <TableContainer><Table size="small">
              <TableHead>
                <TableRow>
                  {step.table!.columns.map((column) => (
                    <TableCell key={column} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {t(columnLabel(column))}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, 20).map((row, index) => (
                  <TableRow key={index}>
                    {step.table!.columns.map((column) => (
                      <TableCell key={column} sx={{ whiteSpace: 'nowrap' }}>
                        {formatCell(row[column])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
            {(rows.length > 20 || step.table!.truncated) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>
                {t('Showing the first {{count}} rows.', { count: Math.min(rows.length, 20) })}
              </Typography>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

function stepLabel(step: ToolStep, t: (key: string, options?: Record<string, unknown>) => string): string {
  const input = (step.input ?? {}) as { doctype?: string; name?: string };
  const doctype = input.doctype ? t(input.doctype) : '';
  switch (step.name) {
    case 'list_doctypes':
      return t('Checked what data you can see');
    case 'describe_doctype':
      return t('Checked the fields of {{doctype}}', { doctype });
    case 'query_documents':
      return t('Looked at {{doctype}}', { doctype });
    case 'get_document':
      return t('Opened a {{doctype}} record', { doctype });
    case 'list_reports':
      return t('Checked the available reports');
    case 'run_report':
      return t('Ran the {{name}} report', { name: input.name ?? '' });
    default:
      return step.name;
  }
}

function errorMessage(
  error: { code: string; message: string },
  t: (key: string) => string,
): string {
  switch (error.code) {
    case 'assistant.not_connected':
      return t('Connect your Claude account to start chatting.');
    case 'assistant.refused':
      return t('This question cannot be answered.');
    case 'assistant.too_long':
      return t('The answer ran too long. Ask a narrower question.');
    case 'assistant.no_credit':
      return t('Your Anthropic account has no credit left. Add credit under Plans & Billing in the Anthropic console, then try again.');
    case 'assistant.key_rejected':
      return t('Anthropic no longer accepts your key. Disconnect and connect a new one.');
    case 'assistant.rate_limited':
      return t('Your Anthropic account has reached its rate limit. Wait a minute and try again.');
    case 'assistant.unavailable':
      return t('Claude is busy right now. Try again in a moment.');
    case 'assistant.budget_exhausted':
      return t("This workspace has used this month's allowance for the data assistant.");
    case 'assistant.model_locked':
    case 'assistant.model_not_allowed':
      return t('This chat uses a model that is no longer available. Start a new chat.');
    default:
      return error.message || t('Something went wrong while answering. Try again.');
  }
}

/**
 * A column name as a person would say it: `branchId__title` → "Branch",
 * `attendanceDate_month` → "Attendance date (month)", `count` → "Count".
 * Run through translation afterwards, so the common ones read in Arabic.
 */
function columnLabel(column: string): string {
  const [base, bucket] = column.replace(/Id__title$/, '').replace(/__title$/, '').split(/_(day|month|year)$/);
  const words = base
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  const label = words.charAt(0).toUpperCase() + words.slice(1);
  return bucket ? `${label} (${bucket})` : label;
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDateTime(value);
  return String(value);
}

/** A saved conversation's turns, with each answer's lookups attached to it. */
function mergeHistory(
  turns: { role: 'user' | 'assistant'; text: string; tools: { name: string; input: unknown }[] }[],
): ChatTurn[] {
  const merged: ChatTurn[] = [];
  let pendingSteps: ToolStep[] = [];
  for (const turn of turns) {
    const steps = turn.tools.map((tool, index) => ({
      id: `${merged.length}-${index}`,
      name: tool.name,
      input: tool.input,
      done: true,
    }));
    if (turn.role === 'user') {
      merged.push({ role: 'user', text: turn.text, steps: [] });
      pendingSteps = [];
      continue;
    }
    pendingSteps = [...pendingSteps, ...steps];
    // An assistant turn that only called tools is folded into the answer that follows.
    if (!turn.text) continue;
    merged.push({ role: 'assistant', text: turn.text, steps: pendingSteps });
    pendingSteps = [];
  }
  return merged;
}
